import { useCallback, useEffect, useState } from "react";
import { TanTableColumn } from "./TanTable";

type Visibility = Record<string, boolean>;

const readVisibility = (storageKey: string, columns: TanTableColumn<any>[], isMobile: boolean): Visibility => {
  const stored = typeof window !== "undefined" ? localStorage.getItem(storageKey) : null;
  if (stored) return JSON.parse(stored);
  const defaults: Visibility = {};
  columns.forEach((col) => {
    if (col.id) defaults[col.id] = !(isMobile && col.hideOnMobile);
  });
  return defaults;
};

// Phones get their own stored column set (`_mobile` key), so hiding columns there never touches the desktop choice
// and an existing desktop choice never overrides the phone defaults. The desktop key is unchanged from before.
export function useColumnVisibility(columns: TanTableColumn<any>[], isMobile = false) {
  const colKeys = columns.map((c) => c.id).join("|");
  const tableStorageKey = `tanTableColumns_${colKeys}${isMobile ? "_mobile" : ""}`;
  const [state, setState] = useState(() => ({
    key: tableStorageKey,
    value: readVisibility(tableStorageKey, columns, isMobile),
  }));

  // Crossing the breakpoint (or new columns) switches to the other stored set within the same render.
  let current = state;
  if (state.key !== tableStorageKey) {
    current = { key: tableStorageKey, value: readVisibility(tableStorageKey, columns, isMobile) };
    setState(current);
  }

  const setColumnVisibility = useCallback(
    (updater: Visibility | ((prev: Visibility) => Visibility)) =>
      setState((prev) => ({
        key: prev.key,
        value: typeof updater === "function" ? updater(prev.value) : updater,
      })),
    [],
  );

  useEffect(() => {
    localStorage.setItem(current.key, JSON.stringify(current.value));
  }, [current.key, current.value]);

  return [current.value, setColumnVisibility] as const;
}
